import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { CX_LOGIN_SUCCESS } from '@cx-web/feature-auth/events';

import {
  type AuthModalStore,
  closeAuthModal,
  createAuthModalStore,
  getActiveAuthModalStore,
  notifyAuthLogin,
  type OpenAtViewOptions,
  openAuthModalAtView,
  setActiveAuthModalStore,
  type View,
} from './store/auth-modal-store';

// Re-exported so existing consumers (and SignupForm/Authentication) keep importing the view contract
// from here; the source of truth now lives in the framework-agnostic store.
export {
  type OpenAtViewOptions,
  type View,
  VIEW_FORGOT_PASSWORD,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_EMAIL,
  VIEW_VERIFY_PAYMENT,
} from './store/auth-modal-store';

export type AuthModalContextValue = {
  close: () => void;
  isOpen: boolean;
  // Monotonic; bumped on each successful login this session. Lets in-tree consumers drive optimistic
  // post-login UI without listening to legacy `OC:*` events.
  loginNonce: number;
  openAtView: (view: View, options?: OpenAtViewOptions) => void;
  prefillEmail?: string;
  setIsOpen: (isOpen: boolean) => void;
  toggle: () => void;
  view: View;
};

// Null default (not a no-op object) so `useAuthModal` throws when used outside a provider: a missing
// provider must fail loudly, never silently no-op (NGF-6331 acceptance criterion).
export const AuthModalContext = createContext<AuthModalContextValue | null>(null);
AuthModalContext.displayName = 'AuthModalContext';

export const AuthModalProvider = ({ children }: PropsWithChildren) => {
  // One store per provider (created lazily) keeps per-request SSR state isolated exactly as useState
  // did; the provider is now a thin adapter that reads via useSyncExternalStore and writes via the
  // store helpers. Mirrors BetslipProvider (NGF-6116).
  const storeRef = useRef<AuthModalStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createAuthModalStore();
  }
  const store = storeRef.current;

  // Publish the live store to non-React consumers (Astro islands, the legacy bridge). The client
  // mounts a single provider, so this pointer always references the active auth modal.
  useEffect(() => {
    setActiveAuthModalStore(store);
    // Clear only if we're still the published store, guarding the transient double-mount window where
    // React runs a new provider's effect before the old provider's cleanup (mirrors BetslipProvider).
    return () => {
      if (getActiveAuthModalStore() === store) setActiveAuthModalStore(null);
    };
  }, [store]);

  // Bridge the client-wide CX_LOGIN_SUCCESS event (dispatched by LoginForm/SignupForm on a successful
  // auth) into the store's loginNonce, so useOptimisticLoginState flips the header to logged-in
  // immediately instead of waiting for a reload to refresh server auth state. This replaces the 2018
  // legacy bridge (deleted in EU-5559) that used to translate the OC widget's OC:LoggedIn event into
  // this same store write — without it, notifyAuthLogin is never called and the navbar only updates on
  // refresh (EU-5591).
  useEffect(() => {
    const onLoginSuccess = () => notifyAuthLogin(store);
    window.addEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    return () => window.removeEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
  }, [store]);

  // getState is a stable reference and returns the same state object until the next setState,
  // satisfying useSyncExternalStore's snapshot contract on both client and server.
  const { isOpen, loginNonce, prefillEmail, view } = useSyncExternalStore(
    store.subscribe,
    store.getState,
    store.getState,
  );

  const close = useCallback(() => closeAuthModal(store), [store]);
  const openAtView = useCallback(
    (view: View, options?: OpenAtViewOptions) => openAuthModalAtView(store, view, options),
    [store],
  );
  // Raw open/close that bypasses openAtView. Clear any leftover prefillEmail when opening so a value
  // from a previous session can't silently seed SignupForm's defaultValue; openAtView is still the
  // path that sets a fresh prefill. (Closing leaves prefill alone — close()/openAtView own that.)
  const setIsOpen = useCallback(
    (isOpen: boolean) => store.setState(isOpen ? { isOpen, prefillEmail: undefined } : { isOpen }),
    [store],
  );
  const toggle = useCallback(() => store.setState(state => ({ isOpen: !state.isOpen })), [store]);

  const value = useMemo<AuthModalContextValue>(
    () => ({ close, isOpen, loginNonce, openAtView, prefillEmail, setIsOpen, toggle, view }),
    [close, isOpen, loginNonce, openAtView, prefillEmail, setIsOpen, toggle, view],
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within a AuthModalProvider');
  }
  return context;
};

/**
 * Optimistic logged-in state for headers that drive the OC auth widget (NGF-6331). Flips to
 * logged-in the moment the widget reports success (`loginNonce` bump) so the account menu updates
 * without waiting for a reload to refresh server auth state, and reverts only when the user dismissed
 * the modal without logging in. `isLoggedIn` is the server-resolved auth state (e.g. RR7's
 * `!!myocData?.myOCUser`, atlas-astro's `isLoggedIn` prop); it seeds the initial value and prevents a
 * revert once the server confirms the session.
 *
 * Shared by RR7 atlas (`LayoutComponents`) and atlas-astro (`CxHeaderWrapper`) so the two headers
 * cannot drift; both already consume `useAuthModal` from this subpath.
 */
export const useOptimisticLoginState = (isLoggedIn: boolean): boolean => {
  const { isOpen: authModalOpen, loginNonce } = useAuthModal();
  const [isOptimisticallyLoggedIn, setIsOptimisticallyLoggedIn] = useState(isLoggedIn);
  // Tracks whether login succeeded during this session, distinguishing "user dismissed without
  // logging in" (revert optimistic state) from "modal closed after a successful login" (keep it,
  // since the server auth state is still stale until the next page load).
  const loginSucceededRef = useRef(false);
  const prevLoginNonceRef = useRef(loginNonce);
  const prevAuthModalOpenRef = useRef(authModalOpen);

  useEffect(() => {
    if (loginNonce === prevLoginNonceRef.current) return;
    prevLoginNonceRef.current = loginNonce;
    loginSucceededRef.current = true;
    setIsOptimisticallyLoggedIn(true);
  }, [loginNonce]);

  // Adopt logged-in state when the server confirms the session out-of-band — e.g. an SPA navigation
  // whose loader returns a now-present `myOCUser` without a full reload. `useState(isLoggedIn)` only
  // captures the mount-time value, so without this the header would render logged-out until reload.
  // `setState(true)` is idempotent (React bails when unchanged), so no extra guard is needed.
  useEffect(() => {
    if (isLoggedIn) setIsOptimisticallyLoggedIn(true);
  }, [isLoggedIn]);

  useEffect(() => {
    const wasOpen = prevAuthModalOpenRef.current;
    prevAuthModalOpenRef.current = authModalOpen;
    if (!wasOpen && authModalOpen) {
      loginSucceededRef.current = false;
    }
    if (wasOpen && !authModalOpen && !loginSucceededRef.current && !isLoggedIn) {
      setIsOptimisticallyLoggedIn(false);
    }
  }, [authModalOpen, isLoggedIn]);

  return isOptimisticallyLoggedIn;
};
